const COUNTDOWN_TIMER_TIME_ZONE = 'America/Los_Angeles';
const COUNTDOWN_TIMER_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/*
 * Milliseconds between UTC and `timeZone` at the given instant. Derived from
 * Intl rather than a fixed offset so PST/PDT is resolved for that instant.
 */
function countdownTimerZoneOffset(epoch, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(epoch));

  const wallClock = {};
  parts.forEach((part) => {
    wallClock[part.type] = part.value;
  });

  const readAsUtc = Date.UTC(
    wallClock.year,
    wallClock.month - 1,
    wallClock.day,
    wallClock.hour,
    wallClock.minute,
    wallClock.second
  );

  return readAsUtc - epoch;
}

/*
 * Turns wall-clock components in `timeZone` into a UTC epoch. The first pass
 * lands within a day of the target, which is close enough to look up the
 * offset actually in effect and correct for it.
 */
function countdownTimerEpoch(fields, timeZone) {
  const naive = Date.UTC(fields.year, fields.month - 1, fields.day, fields.hour, fields.minute, fields.second);
  const approximate = naive - countdownTimerZoneOffset(naive, timeZone);

  return naive - countdownTimerZoneOffset(approximate, timeZone);
}

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.targetTime = this.parseTargetTime();

    if (this.targetTime === null) {
      this.showInvalidState();
      return;
    }

    this.display = this.querySelector('[data-countdown-display]');
    this.expired = this.querySelector('[data-countdown-expired]');
    this.segments = {
      days: this.querySelector('[data-countdown-days]'),
      hours: this.querySelector('[data-countdown-hours]'),
      minutes: this.querySelector('[data-countdown-minutes]'),
      seconds: this.querySelector('[data-countdown-seconds]'),
    };

    this.update();
    this.interval = setInterval(this.update.bind(this), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  parseTargetTime() {
    const match = COUNTDOWN_TIMER_DATE_PATTERN.exec((this.dataset.targetDate || '').trim());
    if (!match) return null;

    return countdownTimerEpoch(
      {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour: Number(match[4] || 0),
        minute: Number(match[5] || 0),
        second: Number(match[6] || 0),
      },
      COUNTDOWN_TIMER_TIME_ZONE
    );
  }

  /*
   * An unparseable date leaves nothing meaningful to count down to, so hide
   * the block rather than freeze a row of zeroes in front of shoppers. In the
   * theme editor, surface the reason instead.
   */
  showInvalidState() {
    const notice = this.querySelector('[data-countdown-invalid]');

    if (!notice) {
      this.hidden = true;
      return;
    }

    const display = this.querySelector('[data-countdown-display]');
    if (display) display.hidden = true;
    notice.hidden = false;
  }

  update() {
    const remaining = this.targetTime - Date.now();

    if (remaining <= 0) {
      this.expire();
      return;
    }

    this.segments.days.textContent = this.pad(Math.floor(remaining / 86400000));
    this.segments.hours.textContent = this.pad(Math.floor((remaining % 86400000) / 3600000));
    this.segments.minutes.textContent = this.pad(Math.floor((remaining % 3600000) / 60000));
    this.segments.seconds.textContent = this.pad(Math.floor((remaining % 60000) / 1000));
  }

  expire() {
    clearInterval(this.interval);
    this.display.hidden = true;

    if (this.expired) this.expired.hidden = false;
  }

  pad(value) {
    return String(value).padStart(2, '0');
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
